import React from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import AppDialog from '../common/AppDialog';
import {
  Business as BusinessIcon,
} from '@mui/icons-material';
import { Company } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';

interface DeleteCompanyDialogProps {
  open: boolean;
  company: Company | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

const DeleteCompanyDialog: React.FC<DeleteCompanyDialogProps> = ({
  open,
  company,
  onClose,
  onConfirm,
  isLoading = false,
  error = null,
}) => {
  const { t } = useLanguage();
  
  const handleConfirm = async () => {
    await onConfirm();
    onClose();
  };

  if (!company) return null;

  return (
    <AppDialog presentation="confirm"
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('companies.deleteCompany')}</DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {t('companies.deleteWarning')}
          </Typography>
        </Alert>

        <Box display="flex" alignItems="center" gap={2} p={2} bgcolor={(theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'} borderRadius={1}>
          <BusinessIcon color="action" />
          <Box>
            <Typography variant="subtitle1" fontWeight="medium">
              {company.name}
            </Typography>
            {company.email && (
              <Typography variant="body2" color="text.secondary">
                {company.email}
              </Typography>
            )}
          </Box>
        </Box>

        <Typography variant="body1" sx={{ mt: 2 }}>
          {t('companies.deleteQuestion')}
        </Typography>
        
        <Box component="ul" sx={{ mt: 1, mb: 0 }}>
          <li>{t('companies.deleteConsequences.allUsers')}</li>
          <li>{t('companies.deleteConsequences.allCustomers')}</li>
          <li>{t('companies.deleteConsequences.allItems')}</li>
          <li>{t('companies.deleteConsequences.allData')}</li>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={isLoading}
          variant="outlined"
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isLoading}
          variant="contained"
          color="error"
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? t('companies.deleting') : t('companies.deleteCompany')}
        </Button>
      </DialogActions>
    </AppDialog>
  );
};

export default DeleteCompanyDialog;
